<template>
	<TheHeader></TheHeader>
	
	<div class="container-fluid">
		<div class="container">
			<form name="form">
				<div class="contain row">
					<TheSidebar></TheSidebar>
					
					<div class="page-container card col-md-8">
						<ThePageHeader :pageTitle="pageTitle"></ThePageHeader>
						
						<div class="row" v-if="pageError == ''">
							
							<div class="row p-3">
								<div class="col-md-6">
									<Button class="p-button-sm" @click="add" label="Create New RPK" :hidden="isViewer" />
								</div>
							</div>
							
							<div class="row p-3 pb-0">
								<label><b>Search existing RPK by:</b></label>
							</div>
							
							<div class="row p-3 pb-0">
								<div class="col-md-6">
									<Dropdown :options="companies" optionLabel="code" optionValue="code" filter :filterFields="['code', 'name']" showClear :placeholder="isCompanyLoading ? ('Loading...') : 'Company'" :loading="isCompanyLoading" v-model="filterCompanyModel.value">
										<template #value="slotProps">
											<div v-if="slotProps.value" class="d-flex align-items-center">
												<span class="col-md-3">{{ slotProps.value }}</span>
												<span class="col-md-9">{{ filterCompanyNameByValue(slotProps.value) }}</span>
											</div>
											<span v-else>
												{{ slotProps.placeholder }}
											</span>
										</template>

										<template #option="slotProps">
											<div class="d-flex align-items-center col-md-12">
												<span class="col-md-3">{{ slotProps.option.code }}</span>
												<span class="col-md-9">{{ slotProps.option.name }}</span>
											</div>
										</template>
									</Dropdown>
								</div>

								<div class="col-md-6">
									<Calendar dateFormat="yy-mm-dd" selectionMode="range" :manualInput="true" placeholder="Balance Sheet Date (range)" v-model="filterBalanceSheetDateModel" />
								</div>
							</div>

							<div class="row p-3">
								<div class="col-md-6">
									<InputText class="p-inputtext" type="text" maxlength="50" placeholder="File ID" v-model="filterFileIdModel.value" />
								</div>

								<div class="col-md-6">
									<Dropdown :options="rpkApplicationStatuses" optionLabel="name" optionValue="value" placeholder="Status" v-model="filterStatusModel.value" />
								</div>
							</div>
							
							<div class="row p-3">
								<div class="col-md-5 d-flex align-items-center">
									<Button class="p-button-sm" @click="find(1)" label="Search"/>
									<span class="p-1"></span>
									<Button class="p-button-sm p-button-secondary" @click="reset" label="Reset"/>
								</div>
									
								<div class="col-md-7">
									<Paginator :first="paginator" :pageLinkSize="3" :rows="pagination.itemsPerPage" :totalRecords="pagination.totalItems" @page="find($event.page + 1)" v-if="pagination.currentPage != 0" />
								</div>
							</div>
							
							<div class="row">
								<div class="col-md-12 p-4">
									<table class="p-datatable p-component p-datatable-hoverable-rows align-middle mb-0 w-12">
										<thead class="p-datatable-thead">
											<tr>
												<th style="width: 8%;">#</th>
												<th style="width: 37%; cursor: pointer;" @click="sort('x.company.name')">Company <span class="pi" :class="[sortingModel.columnName == 'x.company.name' ? (sortingModel.isAscending ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down') : 'pi-fw pi-sort-alt']"></span></th>
												<th style="width: 20%; cursor: pointer;" @click="sort('x.balanceSheetDate')">File ID <span class="pi" :class="[sortingModel.columnName == 'x.balanceSheetDate' ? (sortingModel.isAscending ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down') : 'pi-fw pi-sort-alt']"></span></th>
												<th style="width: 15%;">Status Date</th>
												<th style="width: 10%; cursor: pointer;" @click="sort('x.templateVersion')" class="text-center">Version <span class="pi" :class="[sortingModel.columnName == 'x.templateVersion' ? (sortingModel.isAscending ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down') : 'pi-fw pi-sort-alt']"></span></th>
												<th style="width: 10%;" class="text-center">Status</th>
											</tr>
										</thead>
										<tbody class="p-datatable-tbody" v-if="!isLoading">
											<tr v-for="(companyReport, index) in pagination.companyReports" :key="index" v-on:click="view(companyReport)">
												<td>{{((pagination.currentPage - 1) * pagination.itemsPerPage) + index + 1}}</td>
												<td>{{companyReport.companyName}}</td>
												<td>{{companyReport.fileId}}</td>
												<td>{{companyReport.statusDate}}</td>
												<td class="text-center">{{companyReport.templateVersion}}</td>
												<td class="text-center">
													<Badge class="mr-2" :class="[companyReport.status == 'Accepted' ? 'p-badge-success' : (companyReport.status == 'Rejected' || companyReport.status == 'Cancelled' ? 'p-badge-danger' : (companyReport.status == 'Submitted' ? 'p-badge-primary' : 'p-badge-warning'))]" :value="companyReport.status"></Badge>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
								
								<div class="error-no-record" v-if="!isLoading && pagination.companyReports.length == 0">
									<FontAwesomeIcon :icon="['fas', 'image']" size="2x" />
									<h6><span>No Record Found</span></h6>
								</div>
								
								<TheTableLoading v-if="isLoading"></TheTableLoading>
							</div>
						</div>
						
						<ThePageError @back-event="cancel" :pageError="pageError" v-if="pageError != ''"></ThePageError>
					</div>
					
					<TheFooter></TheFooter>
				</div>
			</form>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from "vue";
	import { useRoute } from "vue-router";
	import store from "@/store";
	import companyApi from "@/api/company-api";
	import companyReportApi from "@/api/companyreport-api";
	import roleMixin from "@/mixin/role";
	import applicationStatusMixin from "@/mixin/application-status";
	import dateUtilsMixin from "@/mixin/utils/date-utils"
	
	import TheHeader from "@/components/TheHeader.vue";
	import TheFooter from "@/components/TheFooter.vue";
	import TheSidebar from "@/components/TheSidebar.vue";
	import ThePageHeader from "@/components/ThePageHeader.vue";
	import TheTableLoading from "@/components/TheTableLoading.vue";
	import ThePageError from "@/components/ThePageError.vue";
	
	export default defineComponent({
		mixins: [roleMixin, applicationStatusMixin, dateUtilsMixin],
		components: {
			TheHeader,
			TheFooter,
			TheSidebar,
			ThePageHeader,
			TheTableLoading,
			ThePageError
		},
		data() {
			return {
				pageError: (store.getters.error == undefined ? '' : store.getters.error),
				companies: [] as any[],
				pagination: (store.getters.getCached.page == null ? {currentPage: 1, itemsPerPage: 10, companyReports: [], totalItems: 0} : store.getters.getCached.page.pagination),
				sortingModel: (store.getters.getCached.page == null ? {columnName: "x.balanceSheetDate", isAscending: false} : store.getters.getCached.page.sortingModel),
				filterCompanyModel: (store.getters.getCached.page == null ? {value: null} : store.getters.getCached.page.filterCompanyModel),
				filterBalanceSheetDateModel: (store.getters.getCached.page == null ? null as any[] | null : store.getters.getCached.page.filterBalanceSheetDateModel),
				filterFileIdModel: (store.getters.getCached.page == null ? {value: ""} : store.getters.getCached.page.filterFileIdModel),
				filterStatusModel: (store.getters.getCached.page == null ? {value: (useRoute().query.status == undefined ? null : useRoute().query.status)} : store.getters.getCached.page.filterStatusModel),
				isLoading: false,
				isCompanyLoading: false,
				currentPublishedVersion: null as any,
			}
		},
		watch: {
			filterLegalGroup(newValue: any) {
				this.pagination = {currentPage: 1, itemsPerPage: 10, companies: [], totalItems: 0};
				this.find(1);
				
				this.loadCompanyList();
			}
		},
		mounted: function() {
			if (this.pageError == '') {	
				this.find(this.pagination.currentPage);

				this.loadCompanyList();
			}
		},
		computed: {
			paginator() {
				return ((this.pagination.currentPage - 1) * this.pagination.itemsPerPage);
			},
			filterLegalGroup() {
				return (store.getters.getFilterLegalGroupModel != null ? store.getters.getFilterLegalGroupModel : null);
			},
			pageTitle() {
				// Append the current published RPK template version after the page title.
				const baseTitle = store.getters.getTitle || 'RPK';
				return (this.currentPublishedVersion != null ? (baseTitle + ' (Published Version ' + this.currentPublishedVersion + ')') : baseTitle);
			}
		},
		methods: {
			loadCompanyList() {
				this.filterCompanyModel = {value: null}
						
				this.isCompanyLoading = true;
				companyApi.companyList({isSheetDialog: true}, this.filterLegalGroup == null ? null : this.filterLegalGroup.identifier)
					.then(response => {
						this.companies = response.data.listSet;
						this.isCompanyLoading = false;
					})
					.catch(error => {
						this.isCompanyLoading = false;
					});
			},
			filterCompanyNameByValue: function (value: any) {
				var companies = this.companies.filter(function (company: any) {
					if (company.code == value) {
						return company;
					}
				});
				if (companies != null && companies.length > 0) {
					return companies[0].name;
				}
				return null;
			},
			sort(columnName: string) {
				if (this.sortingModel.columnName == columnName) {
					this.sortingModel.isAscending = !this.sortingModel.isAscending;
				}
				
				else {
					this.sortingModel.columnName = columnName;
					this.sortingModel.isAscending = true;
				}
				this.find(1);
			},
			find(page: number) {
				this.isLoading = true;

				store.getters.getCached.page = {
					pagination: JSON.parse(JSON.stringify(this.pagination)),
					sortingModel: JSON.parse(JSON.stringify(this.sortingModel)),
					filterCompanyModel: JSON.parse(JSON.stringify(this.filterCompanyModel)),
					filterBalanceSheetDateModel: this.filterBalanceSheetDateModel,
					filterFileIdModel: JSON.parse(JSON.stringify(this.filterFileIdModel)),
					filterStatusModel: JSON.parse(JSON.stringify(this.filterStatusModel)),
				};

				companyReportApi.companyReportFind({
						paginationModel: {currentPage: page},
						sortingModel: this.sortingModel,
						filterCompanyModel: this.filterCompanyModel,
						filterLegalGroupModel: {value: this.filterLegalGroup},
						filterStartBalanceSheetDateModel: {value: (this.filterBalanceSheetDateModel != null && this.filterBalanceSheetDateModel[0] != null ? this.formatToDate(this.filterBalanceSheetDateModel[0]) : null)},
						filterEndBalanceSheetDateModel: {value: (this.filterBalanceSheetDateModel != null && this.filterBalanceSheetDateModel[1] != null ? this.formatToDate(this.filterBalanceSheetDateModel[1]) : null)},
						filterFileIdModel: this.filterFileIdModel,
						filterStatusModel: this.filterStatusModel,
					}, 'rpk')
					.then(response => {
						this.pagination = {currentPage: response.data.currentPage, itemsPerPage: response.data.itemsPerPage, companyReports: response.data.rowSet, totalItems: response.data.totalItems}
						this.currentPublishedVersion = response.data.currentPublishedVersion;
						if (store.getters.getCached.page == null) {
							store.getters.getCached.page = {};
						}
						store.getters.getCached.page.pagination = this.pagination;
						this.isLoading = false;
					})
					.catch(error => {
						this.isLoading = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					})
			},
			reset() {
				this.pagination = {currentPage: 1, itemsPerPage: 10, companyReports: [], totalItems: 0};
				this.sortingModel = {columnName: "x.balanceSheetDate", isAscending: false};
				this.filterCompanyModel = {value: null};
				this.filterBalanceSheetDateModel = null as any[] | null;
				this.filterFileIdModel = {value: ""};
				this.filterStatusModel = {value: null};
				this.find(1);
			},
			add() {
				this.$router.push("/companyreport/rpk/add");
			},
			view(companyReport: any) {
				this.$router.push({ path: "/companyreport/rpk/view/" + companyReport.identifier, state: { from: this.$router.currentRoute.value.fullPath }	});
			}
		}
	});
</script>
